import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  concat,
  filter,
  from,
  ignoreElements,
  interval,
  mergeAll,
  of,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { ServiceStateEnum } from '@mwe/constants';
import { DownloadObservables, Identifier } from '@mwe/models';

const STATE_CHANGE_DELAY = 2000;

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private loadingStateSubject = new BehaviorSubject<Record<Identifier, ServiceStateEnum>>({});
  private readonly loadingState$ = this.loadingStateSubject.asObservable();

  private loadingErrorSubject = new Subject<Record<Identifier, Error>>();
  private readonly loadingError$ = this.loadingErrorSubject.asObservable();

  load<T>({
    identifier,
    observables,
    stateChangeDelay = STATE_CHANGE_DELAY,
    deleteLoadingState = true,
  }: {
    identifier: Identifier;
    observables: DownloadObservables<T>;
    stateChangeDelay?: number;
    deleteLoadingState?: boolean;
  }) {
    // TODO look this up
    return concat(
      of(null).pipe(
        tap(() =>
          this.loadingStateSubject.next({
            ...this.loadingStateSubject.value,
            [identifier]: ServiceStateEnum.LOADING,
          }),
        ),
        ignoreElements(),
      ),
      observables?.[identifier]?.pipe(
        tap(() => {
          this.loadingStateSubject.next({
            ...this.loadingStateSubject.value,
            [identifier]: ServiceStateEnum.SUCCESS,
          });
        }),
        catchError(error => {
          this.handleError(identifier, error);
          return throwError(() => error);
        }),
      ),
      interval(stateChangeDelay).pipe(
        take(1),
        tap(() => {
          if (!deleteLoadingState) return;
          this.loadingStateSubject.next({
            ...this.loadingStateSubject.value,
            [identifier]: ServiceStateEnum.INIT,
          });
        }),
        tap(() => {
          if (!deleteLoadingState) return;
          const value = { ...this.loadingStateSubject.value };
          delete value[identifier];
          this.loadingStateSubject.next(value);
        }),
        ignoreElements(),
      ),
    );
  }

  getLatestMergedLoadingStates(...keys: Identifier[]) {
    const observables = keys.map(key => this.getLoadingState(key));
    return from(observables).pipe(
      mergeAll(),
      filter(state => state !== undefined),
    );
  }

  getLoadingState(identifier: Identifier) {
    return this.loadingState$.pipe(switchMap(stateRecord => of(stateRecord?.[identifier])));
  }

  getErrorState(identifier: Identifier) {
    return this.loadingError$.pipe(switchMap(stateRecord => of(stateRecord?.[identifier])));
  }

  removeErrorState(identifier: Identifier, stateChangeDelay = STATE_CHANGE_DELAY) {
    return interval(stateChangeDelay).pipe(
      take(1),
      tap(() => {
        this.loadingStateSubject.next({
          ...this.loadingStateSubject.value,
          [identifier]: ServiceStateEnum.INIT,
        });
      }),
      tap(() => {
        const value = { ...this.loadingStateSubject.value };
        delete value[identifier];
        this.loadingStateSubject.next(value);
      }),
      ignoreElements(),
    );
  }

  hasFinished(state: ServiceStateEnum): boolean {
    return state === ServiceStateEnum.SUCCESS || state === ServiceStateEnum.ERROR;
  }

  private handleError(identifier: Identifier, error: Error) {
    this.loadingStateSubject.next({ ...this.loadingStateSubject.value, [identifier]: ServiceStateEnum.ERROR });
    this.loadingErrorSubject.next({ [identifier]: error });
  }
}
